import { useEffect, useRef, useState } from 'react';
import { adminApi } from '@/api/admin.api';

export interface LocationBackfillProgress {
  jobId: string;
  target: 'central' | 'resumes_v2' | 'applied';
  organizationId?: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  totalDocs: number;
  processedDocs: number;
  updatedDocs: number;
  unknownLocationDocs: number;
  distinctStrings: number;
  cacheHits: number;
  googleCalls: number;
  failedDocs: number;
  currentIndex?: string;
  error?: string;
  startedAt: string;
  lastUpdated: string;
  finishedAt?: string;
}

export interface BackfillCoverage {
  target: 'central' | 'resumes_v2' | 'applied';
  label: string;
  indexPattern: string;
  indexCount: number;
  totalDocs: number;
  locationChecked: number;
  locationResolved: number;
  yearsExperience: number | null;
}

const POLL_INTERVAL_MS = 2000;

export function LocationBackfill() {
  const [orgs, setOrgs] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  const [activeJob, setActiveJob] = useState<LocationBackfillProgress | null>(null);
  const [recentJobs, setRecentJobs] = useState<LocationBackfillProgress[]>([]);
  const [coverage, setCoverage] = useState<BackfillCoverage[]>([]);
  const [confirmingTarget, setConfirmingTarget] = useState<'resumes_v2' | 'applied' | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = async () => {
    try {
      const [{ active }, { jobs }, { coverage: cov }] = await Promise.all([
        adminApi.getActiveLocationBackfill(),
        adminApi.getRecentLocationBackfills(),
        adminApi.getLocationBackfillCoverage(),
      ]);
      setActiveJob(active);
      setRecentJobs(jobs);
      setCoverage(cov || []);
      return active;
    } catch (e: any) {
      setError(e.message || 'Failed to load backfill status');
      return null;
    }
  };

  useEffect(() => {
    adminApi.listOrganizations().then(setOrgs).catch(() => setOrgs([]));
    refresh();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while a job is running
  useEffect(() => {
    if (activeJob?.status === 'RUNNING' && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const active = await refresh();
        if (active?.status !== 'RUNNING' && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, POLL_INTERVAL_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.status]);

  const start = async (target: 'central' | 'resumes_v2' | 'applied') => {
    setStarting(true);
    setError(null);
    try {
      await adminApi.startLocationBackfill({
        target,
        organizationId: target === 'central' ? selectedOrg : undefined,
        confirm: target === 'resumes_v2' || target === 'applied' ? true : undefined,
      });
      setConfirmingTarget(null);
      setConfirmText('');
      await refresh();
    } catch (e: any) {
      setError(e.message || 'Failed to start backfill');
    } finally {
      setStarting(false);
    }
  };

  const running = activeJob?.status === 'RUNNING';

  return (
    <div>
      <h1 className="text-xl font-semibold mb-1">Data Backfill</h1>
      <p className="text-sm text-gray-500 mb-6">
        Backfill derived fields onto already-indexed resumes: canonical locations (city / state /
        country + coordinates) and, for the Central DB, total years of experience. No re-parse and
        no re-embedding — only these fields are written. Each distinct raw location string is
        geocoded once, ever, via the shared cache. Jobs are idempotent and safe to re-run.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {coverage.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Current coverage
          </h2>
          <table className="w-full text-sm text-left border rounded overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3">Target</th>
                <th className="p-3">Indexes</th>
                <th className="p-3">Documents</th>
                <th className="p-3">Location checked</th>
                <th className="p-3">Location resolved</th>
                <th className="p-3">Years of experience</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((c) => (
                <tr key={c.target} className="border-t">
                  <td className="p-3">
                    <div className="font-medium">{c.label}</div>
                    <code className="text-xs text-gray-500">{c.indexPattern}</code>
                  </td>
                  <td className="p-3">{c.indexCount}</td>
                  <td className="p-3">{c.totalDocs.toLocaleString()}</td>
                  <td className="p-3">
                    <CoverageCell done={c.locationChecked} total={c.totalDocs} />
                  </td>
                  <td className="p-3 text-gray-600">
                    {c.locationResolved.toLocaleString()}
                    {c.locationChecked > c.locationResolved && (
                      <span className="text-xs text-gray-400">
                        {" "}
                        ({(c.locationChecked - c.locationResolved).toLocaleString()} none found)
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    {c.yearsExperience === null ? (
                      <span className="text-xs text-gray-400">n/a</span>
                    ) : (
                      <CoverageCell done={c.yearsExperience} total={c.totalDocs} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 mt-2">
            "Checked" means the pipeline has examined the document; "resolved" means it found a
            real place. The difference is resumes with no detectable location — re-running will
            not change those. Years of experience needs the free-text date parser before it can
            apply to Talent Pool and Applicants.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Central Resume DB */}
        <div className="border rounded-lg p-5">
          <h2 className="font-semibold mb-1">Central Resume Database</h2>
          <p className="text-sm text-gray-500 mb-4">
            Per-org private indexes (<code className="text-xs">*_central_resumes</code>). Owned by
            theRecruit — safe to run any time. Also fills{" "}
            <code className="text-xs">yearsExperience</code>, which powers the experience filter.
          </p>
          <label className="block text-xs uppercase text-gray-500 mb-1">Organization</label>
          <select
            className="w-full border rounded p-2 mb-4 text-sm"
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            disabled={running}
          >
            <option value="all">All organizations</option>
            {orgs
              .filter((o) => o.authOrgId)
              .map((o) => (
                <option key={o.id} value={o.authOrgId}>
                  {o.displayName || o.name} ({o.authOrgId})
                </option>
              ))}
          </select>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            onClick={() => start('central')}
            disabled={running || starting}
          >
            {running ? 'A job is running…' : 'Run Central DB backfill'}
          </button>
        </div>

        {/* resumes_v2 */}
        <div className="border border-amber-300 rounded-lg p-5 bg-amber-50/40">
          <h2 className="font-semibold mb-1">Talent Pool (resumes_v2)</h2>
          <p className="text-sm text-gray-600 mb-4">
            <strong>Writes to the shared live index</strong> that also serves theResume product
            surfaces. Adds <code className="text-xs">location_*</code> fields only; existing
            fields are untouched. Requires explicit confirmation.
          </p>
          {confirmingTarget !== 'resumes_v2' ? (
            <button
              className="bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 disabled:opacity-50"
              onClick={() => setConfirmingTarget('resumes_v2')}
              disabled={running || starting}
            >
              {running ? 'A job is running…' : 'Run resumes_v2 backfill…'}
            </button>
          ) : (
            <div className="border border-amber-400 bg-white rounded p-3">
              <p className="text-sm text-gray-700 mb-2">
                This bulk-updates documents in <code className="text-xs">resumes_v2</code> while it
                serves live traffic. Type <strong>CONFIRM</strong> to proceed.
              </p>
              <input
                className="w-full border rounded p-2 text-sm mb-2"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
              />
              <div className="flex gap-2">
                <button
                  className="bg-amber-600 text-white px-3 py-1.5 rounded text-sm hover:bg-amber-700 disabled:opacity-50"
                  onClick={() => start('resumes_v2')}
                  disabled={confirmText !== 'CONFIRM' || starting}
                >
                  Start backfill
                </button>
                <button
                  className="bg-gray-100 text-gray-700 border px-3 py-1.5 rounded text-sm hover:bg-gray-200"
                  onClick={() => {
                    setConfirmingTarget(null);
                    setConfirmText('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Applied Candidates */}
        <div className="border border-amber-300 rounded-lg p-5 bg-amber-50/40">
          <h2 className="font-semibold mb-1">Applicants (applied_candidates)</h2>
          <p className="text-sm text-gray-600 mb-4">
            <strong>Writes to the live per-job indexes</strong> (
            <code className="text-xs">*_applied_candidates</code>) theResume creates when
            candidates apply. Prefers each candidate's declared location profile before falling
            back to free-text geocoding. Requires explicit confirmation.
          </p>
          {confirmingTarget !== 'applied' ? (
            <button
              className="bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 disabled:opacity-50"
              onClick={() => setConfirmingTarget('applied')}
              disabled={running || starting}
            >
              {running ? 'A job is running…' : 'Run applied candidates backfill…'}
            </button>
          ) : (
            <div className="border border-amber-400 bg-white rounded p-3">
              <p className="text-sm text-gray-700 mb-2">
                This bulk-updates documents across every{' '}
                <code className="text-xs">*_applied_candidates</code> index while they serve live
                traffic. Type <strong>CONFIRM</strong> to proceed.
              </p>
              <input
                className="w-full border rounded p-2 text-sm mb-2"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
              />
              <div className="flex gap-2">
                <button
                  className="bg-amber-600 text-white px-3 py-1.5 rounded text-sm hover:bg-amber-700 disabled:opacity-50"
                  onClick={() => start('applied')}
                  disabled={confirmText !== 'CONFIRM' || starting}
                >
                  Start backfill
                </button>
                <button
                  className="bg-gray-100 text-gray-700 border px-3 py-1.5 rounded text-sm hover:bg-gray-200"
                  onClick={() => {
                    setConfirmingTarget(null);
                    setConfirmText('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {activeJob && <JobCard job={activeJob} title={running ? 'Running job' : 'Latest job'} />}

      {recentJobs.length > 1 && (
        <div className="mt-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Recent runs
          </h2>
          <table className="w-full text-sm text-left border rounded overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3">Started</th>
                <th className="p-3">Target</th>
                <th className="p-3">Org</th>
                <th className="p-3">Status</th>
                <th className="p-3">Processed</th>
                <th className="p-3">Updated</th>
                <th className="p-3">Unknown</th>
                <th className="p-3">Google calls</th>
              </tr>
            </thead>
            <tbody>
              {recentJobs.map((j) => (
                <tr key={j.jobId} className="border-t">
                  <td className="p-3">{new Date(j.startedAt).toLocaleString()}</td>
                  <td className="p-3">{j.target}</td>
                  <td className="p-3 font-mono text-xs">{j.organizationId || '—'}</td>
                  <td className="p-3">
                    <StatusBadge status={j.status} />
                  </td>
                  <td className="p-3">{j.processedDocs}</td>
                  <td className="p-3">{j.updatedDocs}</td>
                  <td className="p-3">{j.unknownLocationDocs}</td>
                  <td className="p-3">{j.googleCalls}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function JobCard({ job, title }: { job: LocationBackfillProgress; title: string }) {
  const pct = job.totalDocs > 0 ? Math.min(100, Math.round((job.processedDocs / job.totalDocs) * 100)) : 0;
  return (
    <div className="border rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">
          {title} —{' '}
          {job.target === 'central'
            ? 'Central DB'
            : job.target === 'applied'
              ? 'Applied Candidates'
              : 'resumes_v2'}
          {job.organizationId ? ` (${job.organizationId})` : ''}
        </h2>
        <StatusBadge status={job.status} />
      </div>

      {job.status === 'RUNNING' && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>
              {job.processedDocs} / {job.totalDocs} documents
              {job.currentIndex ? ` — ${job.currentIndex}` : ''}
            </span>
            <span>{pct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {job.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 text-sm">
          {job.error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-1 text-sm">
        <span className="text-gray-600">Docs processed:</span>
        <span className="font-medium">{job.processedDocs}</span>
        <span className="text-gray-600">Docs updated:</span>
        <span className="font-medium text-green-700">{job.updatedDocs}</span>
        <span className="text-gray-600">Location unknown:</span>
        <span className="font-medium text-amber-700">{job.unknownLocationDocs}</span>
        <span className="text-gray-600">Failed:</span>
        <span className="font-medium text-red-600">{job.failedDocs}</span>
        <span className="text-gray-600">Distinct strings:</span>
        <span className="font-medium">{job.distinctStrings}</span>
        <span className="text-gray-600">Cache hits:</span>
        <span className="font-medium">{job.cacheHits}</span>
        <span className="text-gray-600">New Google calls:</span>
        <span className="font-medium">{job.googleCalls}</span>
        <span className="text-gray-600">Finished:</span>
        <span className="font-medium">
          {job.finishedAt ? new Date(job.finishedAt).toLocaleString() : '—'}
        </span>
      </div>

      {job.status === 'COMPLETED' && job.unknownLocationDocs > 0 && (
        <p className="text-xs text-gray-500 mt-3">
          {job.unknownLocationDocs} resume(s) had no detectable location anywhere. If this number
          is large, a future re-parse pass over the raw files may be worth discussing.
        </p>
      )}
    </div>
  );
}

/** Count plus percentage, coloured green only at full coverage. */
function CoverageCell({ done, total }: { done: number; total: number }) {
  if (total === 0) return <span className="text-xs text-gray-400">—</span>;
  const pct = Math.round((done / total) * 100);
  const complete = done >= total;
  return (
    <span className={complete ? 'text-green-700' : 'text-amber-700'}>
      {done.toLocaleString()} / {total.toLocaleString()}
      <span className="text-xs ml-1">({pct}%)</span>
    </span>
  );
}

function StatusBadge({ status }: { status: LocationBackfillProgress['status'] }) {
  const classes = {
    RUNNING: 'bg-blue-100 text-blue-700',
    COMPLETED: 'bg-green-100 text-green-700',
    FAILED: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${classes[status]}`}>{status}</span>
  );
}
